import {useState} from 'react'
import {useTranslation} from 'react-i18next'
import {useNavigate} from 'react-router-dom'

import {openAlert} from '../../../../../_metronic/elements/NotificationAlert'

import {useErrorHandler} from '../../../../../_metronic/helpers/ErrorHandler'
import {KTSVG} from '../../../../../_metronic/helpers'
import {useIsMobile} from '../../../../shared/responsive'
import * as ReportService from '../services/ReportService'
import {useNDVIReportContext} from './NDVIReportContext'
import {canDeleteNdviReportFromList} from './ndviReportStatus'
import {getNdviReportSourceLabel} from './ndviReportSource'
import './Report.scss'

const Report = ({report}) => {
  const {t} = useTranslation()
  const navigate = useNavigate()
  const isMobile = useIsMobile()
  const {errorHandler} = useErrorHandler()
  const {load} = useNDVIReportContext()
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const canOpenReport = report.status === 'completed'
  const canDeleteFromList = canDeleteNdviReportFromList(report.status)
  const sourceLabel = getNdviReportSourceLabel(report)

  const onClick = () => {
    if (!canOpenReport) {
      const paymentRequired = report.status === 'payment_required'
      openAlert(
        report.status === 'error' || paymentRequired ? 'error' : 'info',
        t(
          paymentRequired
            ? 'reports:ndvi.list.payment_required'
            : report.status === 'error'
            ? 'reports:ndvi.list.failed'
            : 'reports:ndvi.list.still_creating'
        )
      )
      return
    }

    navigate(`/gis/reports/ndvi/view/${report.id}`)
  }

  const onKeyDown = (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return
    }

    event.preventDefault()
    onClick()
  }

  const stopAction = (event) => {
    event.preventDefault()
    event.stopPropagation()
  }

  const requestDelete = (event) => {
    stopAction(event)
    setConfirmDelete(true)
  }

  const cancelDelete = (event) => {
    stopAction(event)
    setConfirmDelete(false)
  }

  const deleteReport = (event) => {
    stopAction(event)
    setDeleting(true)

    errorHandler(ReportService.deleteNdviReport(report.id)).then((res) => {
      if (res.ok) {
        openAlert(
          'success',
          t('reports:ndvi.delete.success.title'),
          t('reports:ndvi.delete.success.message')
        )
        load()
      }

      setDeleting(false)
      setConfirmDelete(false)
    })
  }

  const statusLabel =
    {
      billing_pending: t('reports:ndvi.list.status.billing_pending'),
      completed: t('reports:ndvi.list.status.completed'),
      error: t('reports:ndvi.list.status.error'),
      in_progress: t('reports:ndvi.list.status.in_progress'),
      payment_required: t('reports:ndvi.list.status.payment_required'),
      refund_pending: t('reports:ndvi.list.status.refund_pending'),
      scheduled: t('reports:ndvi.list.status.scheduled'),
    }[report.status] || report.status

  const statusClass =
    {
      billing_pending: 'badge-light-primary',
      completed: 'badge-light-success',
      error: 'badge-light-danger',
      in_progress: 'badge-light-info',
      payment_required: 'badge-light-danger',
      refund_pending: 'badge-light-warning',
      scheduled: 'badge-light-primary',
    }[report.status] || 'badge-light-secondary'

  const deleteAction = canDeleteFromList && (
    <span className='report-item__delete-flow' onClick={(event) => event.stopPropagation()}>
      {!confirmDelete && (
        <button
          type='button'
          className='btn btn-sm btn-light-danger report-item__delete'
          onClick={requestDelete}
          disabled={deleting}
        >
          {t('reports:ndvi.delete.button')}
        </button>
      )}
      {confirmDelete && (
        <span className='report-item__delete-confirmation'>
          <button
            type='button'
            className='btn btn-sm btn-danger report-item__delete-confirm'
            onClick={deleteReport}
            disabled={deleting}
          >
            {t('reports:ndvi.delete.yes_button')}
          </button>
          <button
            type='button'
            className='btn btn-sm btn-light report-item__delete-cancel'
            onClick={cancelDelete}
            disabled={deleting}
          >
            {t('reports:ndvi.delete.no_button')}
          </button>
        </span>
      )}
    </span>
  )

  if (isMobile) {
    return (
      <div
        role='button'
        tabIndex={0}
        onClick={onClick}
        onKeyDown={onKeyDown}
        className='reports-mobile-report-card'
      >
        <span className='reports-mobile-report-card__bar' />
        <span className='reports-mobile-report-card__copy'>
          <span className='reports-mobile-report-card__title'>{report.name}</span>
          <span className={`badge ${statusClass} reports-mobile-report-card__badge`.trim()}>
            {statusLabel}
          </span>
          {sourceLabel && <small>{sourceLabel}</small>}
        </span>
        {deleteAction}
        {canOpenReport && (
          <span className='reports-mobile-report-card__action'>
            <KTSVG path='/media/icons/duotune/arrows/arr024.svg' className='svg-icon svg-icon-2' />
          </span>
        )}
      </div>
    )
  }

  return (
    <div
      role='button'
      tabIndex={0}
      onClick={onClick}
      onKeyDown={onKeyDown}
      className='d-flex report-item align-items-center p-5'
    >
      <span className='bullet bullet-vertical h-40px bg-primary me-5 report-item__bullet'></span>
      <div className='flex-grow-1 report-item__content'>
        <span className='text-gray-800 text-hover-primary fw-bold fs-6 report-item__title'>
          {report.name}
        </span>
        <p className='p-0 d-block report-item__status'>
          <span className={`badge ${statusClass}`.trim()}>{statusLabel}</span>
          {sourceLabel && <span className='badge badge-light-primary ms-2'>{sourceLabel}</span>}
        </p>
      </div>
      {deleteAction}
      {canOpenReport && (
        <span className='btn btn-sm btn-light-primary fs-8 fw-bold report-item__cta'>
          <KTSVG path='/media/icons/duotune/arrows/arr024.svg' />
        </span>
      )}
    </div>
  )
}

export default Report
